import { CheckCircle2, Circle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { IMPORT_STEPS, ImportStep } from "@/lib/import/import-types";

/**
 * Fil d'Ariane des etapes, en lecture seule.
 *
 * Il n'existe pas de composant Stepper dans le repo, et je n'en introduis pas un
 * generique : le wizard est pilote par un tableau d'etapes + un index, comme
 * `ArtmatchIdentifyArtworkJourney`. Ce composant se contente de rendre l'etat
 * visible — quatre etapes aveugles seraient inutilisables.
 */
export const ImportStepper = ({
  current,
  onGoTo,
}: {
  current: ImportStep;
  /** retour en arriere seulement ; l'avance se fait par les boutons d'etape */
  onGoTo?: (step: ImportStep) => void;
}) => {
  const vt = useVisibleT();
  const currentIndex = IMPORT_STEPS.indexOf(current);

  // Libelles courts : le fil d'Ariane doit tenir sur une ligne, les titres
  // complets sont dans l'en-tete de l'etape.
  const labels: Record<ImportStep, string> = {
    table: vt("bo.import.stepShort.table", "Type de fiche"),
    template: vt("bo.import.stepShort.template", "Modèle"),
    upload: vt("bo.import.stepShort.upload", "Fichier"),
    review: vt("bo.import.stepShort.review", "Vérification"),
    relations: vt("bo.import.stepShort.relations", "Liaisons"),
    confirm: vt("bo.import.stepShort.confirm", "Récapitulatif"),
    running: vt("bo.import.stepShort.running", "Import"),
    report: vt("bo.import.stepShort.report", "Rapport"),
  };

  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-1" aria-label={vt("bo.import.step.progress", "Progression de l'import")}>
      {IMPORT_STEPS.map((step, index) => {
        const done = index < currentIndex;
        const active = index === currentIndex;
        const reachable = done && onGoTo != null;

        return (
          <li key={step} className="flex items-center gap-2">
            {index > 0 && (
              <span className="text-gray-300" aria-hidden>
                /
              </span>
            )}
            <button
              type="button"
              disabled={!reachable}
              onClick={reachable ? () => onGoTo(step) : undefined}
              aria-current={active ? "step" : undefined}
              className={cn(
                "flex items-center gap-1.5 text-sm rounded-lg px-1.5 py-1",
                active && "font-semibold text-gray-900",
                done && "text-gray-600",
                !active && !done && "text-gray-400",
                reachable && "hover:underline cursor-pointer",
              )}
            >
              {done ? (
                <CheckCircle2 className="w-4 h-4 text-green-600" aria-hidden />
              ) : (
                <Circle
                  className={cn(
                    "w-4 h-4",
                    active ? "text-gray-900" : "text-gray-300",
                  )}
                  aria-hidden
                />
              )}
              <span>
                {index + 1}. {labels[step]}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
};
